import { useState, type MouseEvent } from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'
import { Link } from 'react-router-dom'
import { COLOR_PRIMARY, COLOR_SKY, NAVBAR_BG } from '../theme'

interface FancyGoldButtonProps {
    to?: string
    href?: string
    color?: string
    onClick?: () => void
    fullWidth?: boolean
    width?: string | number
    endIcon?: React.ReactNode
    disabled?: boolean
    sx?: SxProps<Theme>
    children: React.ReactNode
}

// MUI canonical merge: array form so callers can pass an object, an array, or a theme callback.
const mergeSx = (base: SxProps<Theme>, extra?: SxProps<Theme>): SxProps<Theme> =>
    extra ? [base, ...(Array.isArray(extra) ? extra : [extra])] : base

export function FancyGoldButton({ to, href, color = COLOR_PRIMARY, onClick, fullWidth, width, endIcon, disabled, sx: sxOverride, children }: FancyGoldButtonProps) {
    const [hover, setHover] = useState(false)
    const [ripple, setRipple] = useState({ x: 0, y: 0 })

    const handleMouseEnter = (e: MouseEvent<HTMLElement>) => {
        const rect = e.currentTarget.getBoundingClientRect()
        setRipple({ x: e.clientX - rect.left, y: e.clientY - rect.top })
        setHover(true)
    }

    const sx = {
        ...(fullWidth && { width: '100%' }),
        ...(width !== undefined && { width }),
        position: 'relative',
        overflow: 'hidden',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        py: 1,
        border: `2px solid ${disabled ? 'rgba(0,0,0,0.12)' : color}`,
        borderRadius: '8px',
        backgroundColor: disabled ? 'rgba(0,0,0,0.12)' : color,
        color: disabled ? 'rgba(0,0,0,0.26)' : hover ? COLOR_SKY : NAVBAR_BG,
        fontWeight: 600,
        fontSize: '15px',
        lineHeight: 1.5,
        textDecoration: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'color 0.3s ease',
        letterSpacing: '0.3px',
        userSelect: 'none',
        pointerEvents: disabled ? 'none' : 'auto',
    }

    const inner = (
        <>
            <Box
                component="span"
                sx={{ position: 'absolute', borderRadius: '50%', background: 'white', pointerEvents: 'none' }}
                style={{
                    width: hover ? '1200px' : '0px',
                    height: hover ? '1200px' : '0px',
                    left: ripple.x,
                    top: ripple.y,
                    transform: 'translate(-50%, -50%)',
                    transition: 'width 0.4s ease-in-out, height 0.4s ease-in-out',
                }}
            />
            <Box component="span" className="fancylink" sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {children}
                {endIcon && <Box sx={{ display: 'flex', alignItems: 'center' }}>{endIcon}</Box>}
            </Box>
        </>
    )

    if (href) {
        return (
            <Box component="a" href={href} className="fancybutton" onClick={onClick} onMouseEnter={handleMouseEnter} onMouseLeave={() => setHover(false)} sx={mergeSx(sx, sxOverride)}>
                {inner}
            </Box>
        )
    }

    if (!to) {
        return (
            <Box component="button" className="fancybutton" disabled={disabled} onClick={onClick}
                onMouseEnter={handleMouseEnter} onMouseLeave={() => setHover(false)}
                sx={mergeSx({ ...sx, fontFamily: 'inherit', border: `2px solid ${disabled ? 'rgba(0,0,0,0.12)' : color}` }, sxOverride)}>
                {inner}
            </Box>
        )
    }

    return (
        <Box component={Link} to={to} className="fancybutton" onMouseEnter={handleMouseEnter} onMouseLeave={() => setHover(false)} onClick={onClick} sx={mergeSx(sx, sxOverride)}>
            {inner}
        </Box>
    )
}
